import {inject, Inject, Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import {Message} from '@stomp/stompjs';
import {Store} from '@ngrx/store';
import * as fromRoot from '../store/app.reducers';
import * as OfferActions from '../store/offer/offer.actions';
import * as SalesOfferActions from '../store/sales-offer/sales-offer.actions';
import * as EnforcedAuctionOfferAction from '../store/enforced-auction-offer/enforced-auction-offer.actions';
import {first, takeUntil} from 'rxjs/operators';
import {SystemSettingsService} from './system-settings.service';
import {RxStompService} from './rx-stomp.service';
import {APP_CONFIG} from '../misc/inject-tokens';
import {AppConfig} from '../model/app-config.model';
import {datadogRum} from '@datadog/browser-rum';
import {SalesOfferSignalStore} from '../store/sales-offer/sales-offer.signal-store';
import {BookmarkedOfferSignalStore} from '../store/sales-offer/bookmarked-offer.signal-store';
import {CurrentUserOfferSignalStore} from '../store/sales-offer/current-user-offer.signal-store';

/**
 * This is an improved approach that subscribes/unsubscribes a dealer to individual offers (instead of all offers).
 */
@Injectable({providedIn: 'root'})
export class BidWsUpdateOfferBasedService {
  isUcs636active = false;

  readonly salesOfferStore = inject(SalesOfferSignalStore);
  readonly bookmarkedOffersStore = inject(BookmarkedOfferSignalStore);
  readonly currentUserOffersStore = inject(CurrentUserOfferSignalStore);

  private wsSubscribedOfferIds: number[] = [];
  private wsUnsubscribe: Subject<void> = new Subject<void>();

  constructor(private store: Store<fromRoot.AppState>, private stompService: RxStompService,
              private systemSettingsService: SystemSettingsService,
              @Inject(APP_CONFIG) private config: AppConfig) {
  }

  /**
   * Subscribe dealer to offer bid status update.
   *
   * @param offerId the offer the dealer is subscribing to for bid status updates
   * @param dealerId the dealer subscribing
   * @param unsubscribe subject to unsubscribe this registration
   */
  subscribeBidStatusUpdateTopic(offerId: number, dealerId: number, unsubscribe: Subject<void>): void {
    this.systemSettingsService.isSystemFeatureActivatedForAnyChannel(
        <SystemFeature>'WEB_SOCKET_BID_STATUS_UPDATE')
      .pipe(takeUntil(unsubscribe))
      .subscribe(featureActive => {
        if (featureActive) {
          this.stompService.watch('/topic/offer-bid-status/' + offerId + '/dealer/' + dealerId)
            .pipe(takeUntil(unsubscribe))
            .subscribe(this.consumeBidStatusMessage);
        }
      });
  }

  /**
   * Unsubscribe from the current bid status topic.
   */
  unsubscribeBidStatusUpdateTopic(): void {
    this.wsUnsubscribe.next();
    this.wsUnsubscribe.complete();
    sessionStorage.removeItem('websocket-subscriptions');
  }

  /**
   * Consume message from the stompService (via websockets) to get updates on an offer's bid status.
   *
   * @message: the message received by the backend, containing a BidStatusDto
   */
  public consumeBidStatusMessage = (message: Message) => {
    const wsMessage: WsBaseMessage<any> = JSON.parse(message.body);
    if (wsMessage?.type === 'SUBSCRIBE_CONFIRMATION') {
      //Count number of subscriptions for cypress test (UCSDEV-14445) and ignore otherwise
      //since it is only for performance testing (see UCSDEV-13971)
      let nrOfWsSubscriptions = Number(sessionStorage.getItem('websocket-subscriptions'));
      if (nrOfWsSubscriptions) {
        nrOfWsSubscriptions += 1;
        sessionStorage.setItem('websocket-subscriptions', nrOfWsSubscriptions.toString());
      } else {
        sessionStorage.setItem('websocket-subscriptions', '1');
      }
      return;
    }

    if (this.config.datadogRumEnabled && wsMessage?.type.includes('BID_STATUS_')) {

      let topic = '';
      let offerId = wsMessage.payload.offerId;

      switch (wsMessage.type) {
      case 'BID_STATUS_UPDATE':
        topic = 'consume-bid-status-update';
        break;
      case 'BID_STATUS_GENERAL_UPDATE':
        topic = 'consume-bid-status-general-update';
        break;
      case 'BID_STATUS_DEALER_SPECIFIC_UPDATE':
        topic = 'consume-bid-status-dealer-specific-update';
        offerId = wsMessage.payload.generalStatusDto.offerId;
        break;
      }


      const sessionId = message.headers['message-id'].slice(0, 36);
      const destination = message.headers['destination'];
      const dealerId = destination.split('/')[5];
      const timeStamp = new Date();

      datadogRum.addAction(topic, {
        offerId: offerId,
        sessionId: sessionId,
        dealerId: dealerId,
        timeStamp: timeStamp
      });
    }

    this.systemSettingsService.isSystemFeatureActivatedForAnyChannel(
        <SystemFeature>'UCS_539_SPLIT_WS_BID_STATUS_MESSAGE')
      .subscribe(featureActive => {
        if (featureActive) {
          if (wsMessage?.type === 'BID_STATUS_GENERAL_UPDATE' && wsMessage?.payload.offerId) {
            const bidGeneralStatusDto: BidGeneralStatusDto = wsMessage?.payload;
            this.updateSignalStoresWithGeneralMessage(bidGeneralStatusDto);
            this.store.dispatch(new OfferActions.UpdateBidStatusGeneralAction(bidGeneralStatusDto));
            this.store.dispatch(new SalesOfferActions.UpdateBidStatusGeneralAction(bidGeneralStatusDto));
            this.store.dispatch(new EnforcedAuctionOfferAction.UpdateBidStatusGeneralAction(bidGeneralStatusDto));

          } else if (wsMessage?.type === 'BID_STATUS_DEALER_SPECIFIC_UPDATE' && wsMessage.payload.generalStatusDto.offerId) {
            const bidDealerStatusDto: BidDealerStatusDto = wsMessage?.payload;
            this.updateSignalStoresWithDealerSpecificMessage(bidDealerStatusDto);
            this.store.dispatch(new OfferActions.UpdateBidStatusDealerSpecificAction(bidDealerStatusDto));
            this.store.dispatch(new SalesOfferActions.UpdateBidStatusDealerSpecificAction(bidDealerStatusDto));
            this.store.dispatch(new EnforcedAuctionOfferAction.UpdateBidStatusDealerSpecificAction(bidDealerStatusDto));
          }
        } else {
          /**
             * @deprecated remove when UCS_539_SPLIT_WS_BID_STATUS_MESSAGE is removed
             */
          const bidStatusDto: BidStatusDto = wsMessage?.payload;

          // if message is valid, i.e. contains an offer id, dispatch action to store to update the bid status in offer state
          if (bidStatusDto.offerId) {
            this.store.dispatch(new OfferActions.UpdateBidStatusAction(bidStatusDto));
            this.store.dispatch(new SalesOfferActions.UpdateBidStatusAction(bidStatusDto));
            this.store.dispatch(new EnforcedAuctionOfferAction.UpdateBidStatusAction(bidStatusDto));
          } else {
            console.error('Received stomp message without offer id set in bid status dto, disregarding message.');
          }
        }
      });
  };

  /**
   * Takes an array of offers and subscribes to all auctions for bid status updates via WebSocket
   * if subscriptions are not already available (if they are available, do nothing).
   *
   * @param offers can be of any type, but only auction offers will be used for WebSocket subscription
   * @param currentDealerId the logged-in users current dealer id
   * @param addOnly whether the subscription should simply be added on top of others (if offer is not yet subscribed),
   *                currently used for offer detail page
   */
  public subscribeForBidStatusUpdatesOptimized(offers: OfferDto[], currentDealerId: number, addOnly: boolean) {
    const auctionOfferIds = offers
      .filter(offer => ((offer.status.data === <OfferStatus>'ACTIVE' || offer.status.data === <OfferStatus>'EXTENDED')
            && (offer.offerType.data === <OfferType>'AUCTION' || offer.offerType.data === <OfferType>'ENFORCED_AUCTION')))
      .map(offer => offer.id);

    if (addOnly && auctionOfferIds && auctionOfferIds.length === 1) { // offer detail page
      if (!this.wsSubscribedOfferIds.includes(auctionOfferIds[0])) {
        this.subscribeBidStatusUpdateTopic(auctionOfferIds[0], currentDealerId, this.wsUnsubscribe);
        this.wsSubscribedOfferIds.push(auctionOfferIds[0]);
      }
      // else already subscribed, do nothing
      return;
    }

    let allSubscribed = false;

    // Checks whether there are already ongoing WS subscriptions for ALL qualifying (i.e. auction) offers passed here.
    // This check has been introduced for two reasons:
    // 1) less subscribe/unsubscribe calls within this view (e.g. while filtering for price etc.)
    // 2) fix unnecessary subscribe/unsubscribe calls when changing views (e.g. from bookmarked offer list to offer list),
    // which might be an rxjs bug, as we get double identical aggregation state and double user state objects when loading this view
    // which trigger unnecessary subscribe/unsubscribe calls
    if (this.wsSubscribedOfferIds?.length > 0 && !!auctionOfferIds && auctionOfferIds.length > 0) {
      allSubscribed = auctionOfferIds.every(offerId => this.wsSubscribedOfferIds.includes(offerId));
    }

    if (!allSubscribed && !!auctionOfferIds && auctionOfferIds.length > 0) {
      this.wsUnsubscribe.next();
      this.wsSubscribedOfferIds = [];

      offers
        .filter(offer => ((offer.status.data === <OfferStatus>'ACTIVE' || offer.status.data === <OfferStatus>'EXTENDED')
              && (offer.offerType.data === <OfferType>'AUCTION' || offer.offerType.data === <OfferType>'ENFORCED_AUCTION')))
        .forEach(offer => {
          this.subscribeBidStatusUpdateTopic(offer.id, currentDealerId, this.wsUnsubscribe);
          this.wsSubscribedOfferIds.push(offer.id);
        });
    }
  }

  /**
   * This method is only intended to be used for offer detail page to request websocket subscription as early as
   * possible (while offer is yet to be fetched, hence we only pass the id).
   *
   * This will inevitably lead to subscriptions on buynow offers (instead of just auctions) as well (because we don't
   * know at that point what type the offer will be), but this can probably be neglected as subscriptions will be
   * cleared again anyway later once the user requests new results on offer list.
   *
   * Nonetheless, we only subscribe if offer is not yet part of subscribed offers.
   *
   * @param offerId the detail page's offer id
   * @param currentDealerId the requesting user's current dealer id
   */
  public preflightSubscribeToOffer(offerId: number, currentDealerId: number) {
    if (!this.wsSubscribedOfferIds.includes(offerId)) {
      this.subscribeBidStatusUpdateTopic(offerId, currentDealerId, this.wsUnsubscribe);
      this.wsSubscribedOfferIds.push(offerId);
    }
  }

  private updateSignalStoresWithDealerSpecificMessage(bidDealerStatusDto: BidDealerStatusDto) {
    //We have to check here for the toggle because the correct value is not available in the constructor
    this.systemSettingsService
      .isSystemFeatureActivatedForAnyChannel('UCS_636_STANDARDIZED_SALES_SIDEBAR_FILTER')
      .pipe(first())
      .subscribe(isFeatureActive => {
        this.isUcs636active = isFeatureActive;
        if (this.isUcs636active) {
          this.salesOfferStore.updateOfferBidsDealerSpecific(bidDealerStatusDto);
          this.bookmarkedOffersStore.updateOfferBidsDealerSpecific(bidDealerStatusDto);
          this.currentUserOffersStore.updateOfferBidsDealerSpecific(bidDealerStatusDto);
        }
      });
  }

  private updateSignalStoresWithGeneralMessage(bidGeneralStatusDto: BidGeneralStatusDto) {
    //We have to check here for the toggle because the correct value is not available in the constructor
    this.systemSettingsService
      .isSystemFeatureActivatedForAnyChannel('UCS_636_STANDARDIZED_SALES_SIDEBAR_FILTER')
      .pipe(first())
      .subscribe(isFeatureActive => {
        this.isUcs636active = isFeatureActive;
        if (this.isUcs636active) {
          this.salesOfferStore.updateOfferBidsGeneral(bidGeneralStatusDto);
          this.bookmarkedOffersStore.updateOfferBidsGeneral(bidGeneralStatusDto);
          this.currentUserOffersStore.updateOfferBidsGeneral(bidGeneralStatusDto);
        }
      });
  }
}
